import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { observer } from "mobx-react";
import React, { type JSX } from "react";
import styled, { css } from "styled-components";

import { IBannerData } from "@faire/retailer/lib/bannerUtils";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";

interface IProps<T> {
  renderBanner: (banner: IBannerData<T>) => JSX.Element | null;
  banners: IBannerData<T>[];
  name: string;
}

const BannersBase = <T,>(props: IProps<T>) => {
  const { banners, name, renderBanner } = props;

  return (
    <React.Fragment>
      {banners.map((banner, index) => {
        const renderedBanner = renderBanner(banner);
        if (!renderedBanner) {
          return null;
        }

        if (banner.renderAsModal) {
          return (
            <React.Fragment key={`${name}-${index}`}>
              {renderedBanner}
            </React.Fragment>
          );
        }
        return (
          <BannerTileWrapper
            $size={banner.size}
            $column={banner.column}
            $row={banner.row}
            key={`${props.name}-${index}`}
            data-test-id={banner.type}
            $fullRowBanner={banner.fullRowBanner}
          >
            {renderedBanner}
          </BannerTileWrapper>
        );
      })}
    </React.Fragment>
  );
};

export const Banners = observer(BannersBase);

const BannerTileWrapper = styled.div<{
  $size: number;
  $row: number;
  $column: number;
  $fullRowBanner?: boolean;
}>`
  width: 100%;
  ${() => (isLoggedInRetailer() ? undefined : "padding: 8px 4px")};

  ${responsiveMediaQueries.tabletAndAbove} {
    ${() =>
      isLoggedInRetailer()
        ? undefined
        : css`
            padding: ${getSpacing("1x", "1x", 0, "1x")};
          `};
  }

  ${(props) => css`
    grid-column: ${props.$fullRowBanner
      ? "start/end"
      : css`
          ${props.$column} / span ${props.$size}
        `};
    grid-row: ${props.$row} / span 1;
  `}
`;
